import { formatCurrency } from "@angular/common";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { SelectItem } from "primeng/api";
import { AchPaymentMethod, Card, Payment } from "src/app/models/payment.model";
import { ConfigService, PaymentMethodsService } from "src/app/services";
import { CreditCardPipe } from "src/app/shared/credit-card.pipe";
import { LabelValuePair, ScrBaseComponent } from "../../base.component";
import { CheckoutState, FormGroupControls } from "./checkout.payment.component";
import { range } from 'lodash-es';
import { BehaviorSubject, Subscription } from "rxjs";
import { CheckboxChangeEvent } from "primeng/checkbox";

export interface PaymentMethodWrapper {
  disabled?: boolean;
  selected: boolean;
  description: string;
  id: number;
  ach?: AchPaymentMethod;
  cc?: Card;

}
@Component({
  selector: 'app-paymentinformation',
  templateUrl: './payment.information.component.html'
})
export class PaymentInformationComponent extends ScrBaseComponent implements OnInit, OnDestroy {

  @Input() submitted = false;
  @Input() form!: FormGroup;

  @Input() checkoutState!: BehaviorSubject<CheckoutState>;

  public credit = 0;
  public minOfCreditAndTotal: string | null = null;
  public methods: PaymentMethodWrapper[] = [];
  public selectedMethod?: PaymentMethodWrapper;
  public showNewPaymentMethod = false;
  public achIdentificationCode!: string;
  public showAccountMismatchError = false;
  public achMaxPaymentLimit = 0;
  public showPaymentMethods = true;

  public applyCredit = false;
  public totalPrice = 0;
  private subscription?: Subscription;

  cardTypes: LabelValuePair[] = [];
  months: SelectItem<number>[] = [];
  years: SelectItem<number>[] = [];
  accountTypes: LabelValuePair[] = [
    { label: "Checking", value: "Checking" },
    { label: "Corporate Checking", value: "CorporateChecking" },
    { label: "Savings", value: "Saving" }
  ];
  creditCardRequiredFields = ['cardType', 'creditCardNumber', 'expirationMonth', 'expirationYear', 'cvv'];
  constructor(private paymentMethodsService: PaymentMethodsService, private configService: ConfigService) {
    super();
  }

  formGroup(): FormGroupControls {
    return {
      paymentMode: new FormControl<string>({ value: 'payNow', disabled: true }, [Validators.required]),
      selectedMethod: new FormControl<number>(0, [Validators.required]),
      newPaymentMethod: new FormControl<number>(0, []),
      // workaround for disabled radio buttons in cc list.
      disabledRadio: new FormControl<number>({ value: 0, disabled: true }, []),
      cc: new FormGroup({
        description: new FormControl<string>('', []),
        cardType: new FormControl<string>('', [Validators.required]),
        creditCardNumber: new FormControl<string>('', [Validators.required]),
        expirationMonth: new FormControl<string>('', [Validators.required]),
        expirationYear: new FormControl<string>('', [Validators.required]),
        cvv: new FormControl<string>('', [Validators.required]),
        addCardToProfileAfterCheckout: new FormControl<boolean>(false, []),
        default: new FormControl<boolean>({ value: false, disabled: true }, []),
      }),
      ach: new FormGroup({
        accountOwnerName: new FormControl<string>('', []),
        accountType: new FormControl<string>('CorporateChecking', []),
        bankName: new FormControl<string>('', []),
        description: new FormControl<string>('', []),
        accountNumber: new FormControl<string>('', [Validators.minLength(6)]),
        confirmAccountNumber: new FormControl<string>('', [Validators.required, Validators.minLength(6)]),
        routingNumber: new FormControl<string>('', [Validators.required, Validators.minLength(9)]),
        limit: new FormControl<string>('', [Validators.required]),
        addToProfileAfterCheckout: new FormControl<boolean>(false, []),
        defaultPaymentMethod: new FormControl<boolean>({ value: false, disabled: true }, []),
        consent: new FormControl<boolean>(false, [Validators.requiredTrue])
      })
    };
  }

  getSelectedPaymentMethod(payment: Payment): [ach?: AchPaymentMethod, cc?: Card] | undefined {
    const sel = this.methods.find(m => m.selected);
    if (!sel) {
      return undefined;
    }

    // if payment methods are not showing, user does not need to pay so we
    // don't return a selected payment method.
    if (!this.showPaymentMethods) {
      return [undefined, undefined];
    }

    if (sel.id >= 0) {
      return [sel.ach, sel.cc];
    }
    else {
      const newType = this.form.get('newPaymentMethod')?.value;
      if (newType === 0) {
        const cc = new Card();
        Object.assign(cc, this.form.get('cc')?.getRawValue());
        cc.address1 = payment.billingAddress1 ?? '';
        cc.address2 = payment.billingAddress2;
        cc.city = payment.billingCity ?? '';
        cc.state = payment.billingState ?? '';
        cc.country = payment.billingCountry ?? '';
        cc.firstName = payment.billingFirstName ?? '';
        cc.lastName = payment.billingLastName ?? '';
        cc.zip = payment.billingZip || '';

        return [undefined, cc];
      }
      else if (newType === 1) {
        const ach = new AchPaymentMethod();
        Object.assign(ach, this.form.get('ach')?.getRawValue());
        return [ach, undefined];
      }
      else {
        this.log.error('new type is ' + newType);
      }
    }
    return undefined;
  }
  addToProfileUpdate(e: CheckboxChangeEvent): void {
    if (!e.checked) {
      // if add to profile is false, set default to false as well.
      this.form.get('cc.default')?.setValue(false);
      this.form.get('cc.default')?.disable();
      this.form.get('ach.defaultPaymentMethod')?.setValue(false);
      this.form.get('ach.defaultPaymentMethod')?.disable();
    }
    else {
      this.form.get('ach.defaultPaymentMethod')?.enable();
      this.form.get('cc.default')?.enable();
    }
  }

  /**
   * See that this is called.
   * @returns 
   */
  validateBeforeSubmit(): boolean {
    // showAccountMismatchError
    return false;
  }


  applyCreditUpdated() {
    this.checkoutState.next({
      applyCredit: this.applyCredit
    });
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscription?.unsubscribe();
  }
  ngOnInit(): void {

    this.cardTypes = [
      { label: "Visa", value: "Visa" },
      { label: "Mastercard", value: "Mastercard" },
      { label: "Discover", value: "Discover" },
      { label: "American Express", value: "Amex" }
    ];

    this.months = range(1, 13).map(v => {
      return { label: `${v}`.padStart(2, '0'), value: v };
    });

    const thisYear = new Date().getFullYear();
    this.years = range(12).map(v => {
      return { label: `${thisYear + v}`, value: thisYear + v };
    });

    this.configService.getConfig().subscribe(config => {
      this.achIdentificationCode = config.achIdentificationCode;
      this.achMaxPaymentLimit = config.achMaxPaymentLimit;
    });

    this.subscription = this.checkoutState.subscribe(upd => {
      if (upd.applyCredit !== undefined) {
        this.applyCredit = upd.applyCredit;
      }
      if (upd.credit !== undefined) {
        this.credit = upd.credit;
      }

      if (upd.totalPrice !== undefined) {
        this.totalPrice = upd.totalPrice;
      }
      this.minOfCreditAndTotal = formatCurrency(Math.min(this.credit, this.totalPrice), 'en-US', '$');

      if (upd.amountDueAfterCredit !== undefined) {
        this.showPaymentMethods = upd.amountDueAfterCredit !== 0;
        if (!this.showPaymentMethods) {
          this.clearValidators();
        }
      }
    });

    this.observe(this.paymentMethodsService.retrievePaymentMethods(), rsp => {

      this.form.controls.paymentMode.setValue('payNow');

      this.methods = [];
      if (rsp.ach) {
        rsp.ach.forEach(ach => {
          const ml = formatCurrency(ach.limit, 'en-US', '$');
          const item = {
            id: ach.achPaymentMethodId,
            selected: ach.defaultPaymentMethod === true,
            description: `ACH ${ach.bankName} ****${ach.lastFourDigits} ${ach.description || ''} (Max Limit: ${ml}) ${ach.defaultPaymentMethod ? ' (Default)' : ''}`,
            ach
          };
          this.methods.push(item);
        });
      }
      if (rsp.creditcard) {
        rsp.creditcard.forEach(cc => {
          const mm = `${cc.expirationMonth}`.padStart(2, '0');
          const expired = this.isExpired(cc);
          const item = {
            id: cc.creditCardId,
            disabled: expired,
            selected: cc.default === true,
            description: `${new CreditCardPipe().transform(cc.cardType)} ****${cc.lastFourDigits} ` +
              `(${expired ? 'Expired ' : ''}${mm}/${cc.expirationYear}) ` +
              `${cc.description || ''}${cc.default ? ' (Default)' : ''}`,
            cc
          };
          this.methods.push(item);
        });
      }

      const isThereADefault = this.methods.find(m => m.selected && !m.disabled);

      this.methods.push(
        {
          id: -1,
          description: 'New Payment Method',
          selected: isThereADefault === undefined
        }
      );

      // if we found a default payment method, select it.
      if (isThereADefault) {
        this.form.controls.selectedMethod.setValue(isThereADefault.id);
        this.choosePM(isThereADefault);
      }
      else {
        let def = 0;
        // choose the first if it is enabled.
        if (this.methods[def].disabled) {
          // can't choose the disabled one.
          def = this.methods.length - 1;
        }
        this.form.controls.selectedMethod.setValue(this.methods[def].id);
        this.choosePM(this.methods[def]);
      }

      return false;
    });
  }

  clearValidators() {
    this.creditCardRequiredFields.forEach((fields: string) => {
      this.form.get('cc')?.get(fields)?.clearValidators();
      this.form.get('cc')?.get(fields)?.updateValueAndValidity();
    });
  }

  isNotExpired(pm: PaymentMethodWrapper): boolean {
    if (!pm || !pm.cc) {
      return false;
    }
    return !this.isExpired(pm.cc);
  }

  chooseNewPaymentMethod() {
    const sel = this.methods.find(m => m.selected);
    if (sel) {
      this.choosePM(sel);
    }
  }

  private setValAndUpdate(fg: FormGroup, k: string, validators: ValidatorFn | ValidatorFn[] | null): void {
    const ctrl = fg.get(k);
    if (!ctrl) {
      this.log.error('could not find ' + k);
      return;
    }
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  choosePM(pm: PaymentMethodWrapper): void {
    this.methods.forEach(m => m.selected = false);
    pm.selected = true;

    this.showNewPaymentMethod = pm.id === -1;
    if (this.showNewPaymentMethod) {

      const ccfg = this.form.get('cc') as FormGroup;
      const achfg = this.form.get('ach') as FormGroup;

      const newType = this.form.get('newPaymentMethod')?.value;
      if (newType === 0) {
        // CC required
        achfg.updateValueAndValidity({ onlySelf: false, emitEvent: true });

        Object.keys(achfg.controls).forEach(k => {
          const ctrl = achfg.get(k);
          if (!ctrl) {
            this.log.error('could not find ' + k);
          }
          else {
            ctrl.removeValidators([Validators.required, Validators.requiredTrue]);
            ctrl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
          }
        });
        this.setValAndUpdate(ccfg, 'cardType', [Validators.required]);
        this.setValAndUpdate(ccfg, 'creditCardNumber', [Validators.required]);
        this.setValAndUpdate(ccfg, 'expirationMonth', [Validators.required]);
        this.setValAndUpdate(ccfg, 'expirationYear', [Validators.required]);
        this.setValAndUpdate(ccfg, 'cvv', [Validators.required]);
      }
      else {
        ccfg.updateValueAndValidity({ onlySelf: false, emitEvent: true });
        Object.keys(ccfg.controls).forEach(k => {
          const ctrl = ccfg.get(k);
          if (!ctrl) {
            this.log.error('could not find ' + k);
          }
          else {
            ctrl.removeValidators([Validators.required, Validators.requiredTrue]);
            ctrl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
          }
        });
        this.setValAndUpdate(achfg, 'accountOwnerName', Validators.required);
        this.setValAndUpdate(achfg, 'accountType', Validators.required);
        this.setValAndUpdate(achfg, 'accountNumber', [Validators.required, Validators.minLength(6)]);
        this.setValAndUpdate(achfg, 'confirmAccountNumber', [Validators.required, Validators.minLength(6), this.checkit('accountNumber')]);
        this.setValAndUpdate(achfg, 'routingNumber', [Validators.required, Validators.minLength(9)]);
        this.setValAndUpdate(achfg, 'limit', Validators.required);
        this.setValAndUpdate(achfg, 'bankName', Validators.required);
        this.setValAndUpdate(achfg, 'consent', Validators.requiredTrue);
      }
    }
    else {
      const ccfg = this.form.get('cc') as FormGroup;
      Object.keys(ccfg.controls).forEach(k => {
        ccfg.get(k)?.setValidators([]);
        ccfg.get(k)?.updateValueAndValidity();
      });
      const achfg = this.form.get('ach') as FormGroup;
      Object.keys(achfg.controls).forEach(k => {
        achfg.get(k)?.setValidators([]);
        achfg.get(k)?.updateValueAndValidity();
      });

    }
  }

  isExpired({ expirationMonth, expirationYear }: Card): boolean {
    const today = new Date();

    const month = today.getMonth() + 1;
    const year = today.getFullYear();

    const yyyymm = (month: number, year: number): number => {
      const mm = `${month}`.padStart(2, '0');
      return +`${year}${mm}`;
    };

    return yyyymm(month, year) > yyyymm(expirationMonth, expirationYear);
  }


  checkit(other: string): ValidatorFn {

    return (control: AbstractControl): (ValidationErrors | null) => {
      // console.debug('same validator running with ' + control + ' other field ' + other);
      const achfg = this.form.get('ach') as FormGroup;
      const v = achfg.get(other)?.value;
      const valid = (v === control.value);
      return valid ? null : { appExactMatch: true };
    };
  }

}